import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Access.css';
import './Cities.css';

const COUNT_COLUMNS = [
  { key: 'hotels', label: 'Hotels' },
  { key: 'spas', label: 'Spas' },
  { key: 'tours', label: 'Tours' },
  { key: 'travelPackages', label: 'Packages' },
];

const EMPTY_TARGET = { city: '', state: '', country: '', cityLatitude: '', cityLongitude: '' };

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const formatCoordinates = (lat, lng) =>
  lat == null || lng == null || lat === '' || lng === ''
    ? null
    : `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const hasCoordinate = (value, limit) => {
  if (value === '' || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isFinite(number) && number !== 0 && Math.abs(number) <= limit;
};

const reasonLabel = (reason, group) => {
  if (reason === 'multiple-spellings') return plural(group.variants.length, 'spelling');
  if (reason === 'not-a-city-name') return 'Area or landmark saved as city';
  if (reason === 'missing-coordinates') return `${plural(group.missingCoordinates, 'listing')} without city coordinates`;
  return reason;
};

const Cities = () => {
  const { hasPermission } = useContext(AuthContext);
  const canUpdate = hasPermission('cities.update');

  const [variants, setVariants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ spellings: 0, cities: 0, needsReview: 0 });
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busyMergeId, setBusyMergeId] = useState(null);

  // Merge dialog
  const [mergeSources, setMergeSources] = useState(null); // null = closed
  const [target, setTarget] = useState(EMPTY_TARGET);
  const [cityQuery, setCityQuery] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const lookupRef = useRef(0);

  const load = useCallback(async () => {
    try {
      setError('');
      const [citiesResponse, mergesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/cities`),
        axios.get(`${API_BASE_URL}/admin/cities/merges`),
      ]);
      const data = citiesResponse.data?.data || {};
      setVariants(data.variants || []);
      setGroups(data.groups || []);
      setTotals(data.totals || { spellings: 0, cities: 0, needsReview: 0 });
      setMerges(mergesResponse.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const variantByCity = useMemo(() => {
    const map = new Map();
    variants.forEach((variant) => map.set(variant.city, variant));
    return map;
  }, [variants]);

  const reviewGroups = useMemo(() => groups.filter((group) => group.needsReview), [groups]);

  const filteredVariants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter((variant) =>
      variant.city.toLowerCase().includes(q) || (variant.state || '').toLowerCase().includes(q)
    );
  }, [variants, search]);

  const pagination = usePagination(filteredVariants, { noun: 'city spellings' });

  const toggleSelected = (city) => {
    setSelected((current) =>
      current.includes(city) ? current.filter((value) => value !== city) : [...current, city]
    );
  };

  // ---- merge dialog ----

  const openMerge = (sources, suggested) => {
    setMergeSources(sources);
    setTarget({
      city: suggested?.city || '',
      state: suggested?.state || '',
      country: suggested?.country || '',
      cityLatitude: suggested?.cityLatitude ?? '',
      cityLongitude: suggested?.cityLongitude ?? '',
    });
    setCityQuery('');
    setCitySuggestions([]);
    setPreview(null);
    setDialogError('');
    setNotice('');
  };

  const openMergeForSelection = () => {
    const chosen = selected.map((city) => variantByCity.get(city)).filter(Boolean);
    if (chosen.length === 0) return;
    // Start from the spelling that already reads like a proper city name
    const best = [...chosen].sort((a, b) =>
      (Number(b.isClean) - Number(a.isClean)) ||
      (Number(b.city.includes(',')) - Number(a.city.includes(','))) ||
      (b.total - a.total)
    )[0];
    const group = groups.find((entry) => entry.variants.includes(best.city));
    openMerge(chosen.map((variant) => variant.city), group?.suggested || best);
  };

  const closeMerge = () => {
    if (merging) return;
    setMergeSources(null);
  };

  const updateTarget = (patch) => {
    setTarget((current) => ({ ...current, ...patch }));
    // The preview no longer describes what would be written
    setPreview(null);
  };

  const removeSource = (city) => {
    setMergeSources((current) => current.filter((value) => value !== city));
    setPreview(null);
  };

  const keepVariantSpelling = (variant) => {
    updateTarget({
      city: variant.city.trim(),
      state: variant.state || '',
      country: variant.country || '',
      cityLatitude: variant.cityLatitude ?? '',
      cityLongitude: variant.cityLongitude ?? '',
    });
  };

  const handleCityQueryChange = async (e) => {
    const value = e.target.value;
    setCityQuery(value);
    if (value.trim().length < 3) {
      setCitySuggestions([]);
      return;
    }
    const lookup = ++lookupRef.current;
    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
        params: { input: value, types: 'cities' },
      });
      if (lookup === lookupRef.current) setCitySuggestions(response.data?.data || []);
    } catch (err) {
      if (lookup === lookupRef.current) {
        setCitySuggestions([]);
        setDialogError(err?.response?.data?.message || 'City search is unavailable. Fill the fields below by hand.');
      }
    }
  };

  const handleCityPick = async (place) => {
    setCitySuggestions([]);
    setCityQuery('');
    setDialogError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id },
      });
      const details = response.data?.data || {};
      updateTarget({
        city: details.canonicalCity || place.description,
        state: details.state || '',
        country: details.country || '',
        cityLatitude: details.latitude ?? '',
        cityLongitude: details.longitude ?? '',
      });
    } catch (err) {
      setDialogError('Could not load that city. Try again or fill the fields by hand.');
    }
  };

  const targetReady =
    target.city.trim() !== '' &&
    hasCoordinate(target.cityLatitude, 90) &&
    hasCoordinate(target.cityLongitude, 180);

  const mergeBody = () => ({
    sources: mergeSources,
    target: {
      city: target.city.trim(),
      state: target.state.trim(),
      country: target.country.trim(),
      cityLatitude: Number(target.cityLatitude),
      cityLongitude: Number(target.cityLongitude),
    },
  });

  const handlePreview = async () => {
    setPreviewing(true);
    setDialogError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/cities/merge/preview`, mergeBody());
      setPreview(response.data?.data || null);
    } catch (err) {
      setPreview(null);
      setDialogError(err?.response?.data?.message || 'Failed to preview the merge');
    } finally {
      setPreviewing(false);
    }
  };

  const handleMerge = async () => {
    if (!preview || preview.changedCount === 0) return;
    const question =
      `Move ${plural(preview.changedCount, 'listing')} to "${target.city.trim()}"?\n\n` +
      'The previous values are saved, and the merge can be undone from Merge history.';
    if (!window.confirm(question)) return;

    setMerging(true);
    setDialogError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/cities/merge`, {
        ...mergeBody(),
        expectedCount: preview.changedCount,
      });
      setNotice(response.data?.message || 'Cities merged');
      setMergeSources(null);
      setSelected([]);
      await load();
    } catch (err) {
      setDialogError(err?.response?.data?.message || 'Failed to merge cities');
      // The data moved under the preview: make the admin look again
      if (err?.response?.status === 409) setPreview(null);
    } finally {
      setMerging(false);
    }
  };

  // ---- history ----

  const handleDownloadBackup = async (merge) => {
    setBusyMergeId(merge._id);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/cities/merges/${merge._id}`);
      const blob = new Blob([JSON.stringify(response.data?.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `city-merge-${String(merge.createdAt || '').slice(0, 10)}-${merge._id}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to download the backup');
    } finally {
      setBusyMergeId(null);
    }
  };

  const handleUndo = async (merge) => {
    const question =
      `Undo this merge? ${plural(merge.changedCount, 'listing')} will get back the city ` +
      `they had before being moved to "${merge.target?.city}".`;
    if (!window.confirm(question)) return;

    setBusyMergeId(merge._id);
    setError('');
    setNotice('');
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/cities/merges/${merge._id}/undo`);
      setNotice(response.data?.message || 'Merge undone');
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to undo the merge');
    } finally {
      setBusyMergeId(null);
    }
  };

  const renderCounts = (variant) =>
    COUNT_COLUMNS.map((column) => (
      <td key={column.key} className="cities-number">
        {variant.counts?.[column.key] || <span className="access-muted">—</span>}
      </td>
    ));

  const renderMergeDialog = () => {
    const sourceVariants = mergeSources.map((city) => variantByCity.get(city) || { city, total: 0 });
    const changedItems = preview ? preview.items.filter((item) => item.changed) : [];

    return (
      <div className="customers-modal" onClick={closeMerge}>
        <div className="customers-modal-content cities-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="customers-modal-header">
            <h2>Merge city spellings</h2>
            <button type="button" className="modal-close" onClick={closeMerge} aria-label="Close">×</button>
          </div>

          <div className="customers-modal-body">
            {dialogError && <div className="error-message">{dialogError}</div>}

            <div className="cities-step">
              <div className="cities-step-title">1. Spellings to merge</div>
              <p className="cities-step-hint">
                Every listing saved under these spellings moves to the correct city below.
                Remove any that is a different place.
              </p>
              <ul className="cities-source-list">
                {sourceVariants.map((variant) => (
                  <li key={variant.city}>
                    <div>
                      <span className="cities-source-name">{variant.city}</span>
                      <span className="access-muted"> · {plural(variant.total, 'listing')}</span>
                    </div>
                    <div className="cities-source-actions">
                      <button type="button" className="cities-link" onClick={() => keepVariantSpelling(variant)}>
                        Keep this spelling
                      </button>
                      {mergeSources.length > 1 && (
                        <button type="button" className="cities-link cities-link-danger" onClick={() => removeSource(variant.city)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="cities-step">
              <div className="cities-step-title">2. Correct city</div>
              <p className="cities-step-hint">
                Search the city to fill its proper name and coordinates, or keep one of the spellings above.
              </p>
              <div className="cities-search">
                <input
                  type="text"
                  className="access-input"
                  value={cityQuery}
                  onChange={handleCityQueryChange}
                  placeholder="Search city, e.g. Khatoo"
                />
                {citySuggestions.length > 0 && (
                  <div className="cities-suggestions">
                    {citySuggestions.map((suggestion) => (
                      <button type="button" key={suggestion.place_id} onClick={() => handleCityPick(suggestion)}>
                        <strong>{suggestion.structured_formatting?.main_text || suggestion.description}</strong>
                        <span>{suggestion.structured_formatting?.secondary_text || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="cities-target-grid">
                <label className="cities-field cities-field-wide">
                  <span>City name *</span>
                  <input
                    type="text"
                    className="access-input"
                    value={target.city}
                    onChange={(e) => updateTarget({ city: e.target.value })}
                    placeholder="City, State, Country"
                  />
                </label>
                <label className="cities-field">
                  <span>State</span>
                  <input
                    type="text"
                    className="access-input"
                    value={target.state}
                    onChange={(e) => updateTarget({ state: e.target.value })}
                  />
                </label>
                <label className="cities-field">
                  <span>Country</span>
                  <input
                    type="text"
                    className="access-input"
                    value={target.country}
                    onChange={(e) => updateTarget({ country: e.target.value })}
                  />
                </label>
                <label className="cities-field">
                  <span>City latitude *</span>
                  <input
                    type="number"
                    step="any"
                    className="access-input"
                    value={target.cityLatitude}
                    onChange={(e) => updateTarget({ cityLatitude: e.target.value })}
                  />
                </label>
                <label className="cities-field">
                  <span>City longitude *</span>
                  <input
                    type="number"
                    step="any"
                    className="access-input"
                    value={target.cityLongitude}
                    onChange={(e) => updateTarget({ cityLongitude: e.target.value })}
                  />
                </label>
              </div>
              {!targetReady && (
                <p className="cities-step-hint cities-warning">
                  The city name and both coordinates are needed before you can preview.
                </p>
              )}
            </div>

            <div className="cities-step">
              <div className="cities-step-title">3. Preview, then merge</div>
              {!preview ? (
                <p className="cities-step-hint">Nothing is changed until you press Merge.</p>
              ) : (
                <>
                  <p className="cities-step-hint">
                    <strong>{plural(preview.changedCount, 'listing')}</strong> will change
                    {preview.unchangedCount > 0 && `, ${preview.unchangedCount} already correct`}.
                    Only the city, state, country and city coordinates are rewritten.
                  </p>
                  {changedItems.length > 0 && (
                    <div className="cities-preview">
                      <table className="access-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Listing</th>
                            <th>City now</th>
                            <th>City coordinates now</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changedItems.map((item) => (
                            <tr key={`${item.kind}-${item.listing}`}>
                              <td>{item.typeLabel}</td>
                              <td className="access-name">{item.name || '—'}</td>
                              <td>
                                {item.before.city === item.after.city
                                  ? <span className="access-muted">Same name</span>
                                  : item.before.city}
                              </td>
                              <td>
                                {formatCoordinates(item.before.cityLatitude, item.before.cityLongitude) ||
                                  <span className="access-tag access-tag-inactive">Missing</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="cities-dialog-footer">
              <button type="button" className="btn btn-secondary" onClick={closeMerge} disabled={merging}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePreview}
                disabled={!targetReady || previewing || merging}
              >
                {previewing ? 'Checking…' : preview ? 'Preview again' : 'Preview changes'}
              </button>
              {canUpdate && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleMerge}
                  disabled={!preview || preview.changedCount === 0 || merging}
                >
                  {merging
                    ? 'Merging…'
                    : preview && preview.changedCount > 0
                      ? `Merge ${plural(preview.changedCount, 'listing')}`
                      : 'Merge'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>Cities</h1>
            <p>Find a city saved under more than one spelling and merge it into one, so search shows every listing.</p>
          </div>
          <div className="cities-stats">
            <span className="access-badge">Cities: {totals.cities}</span>
            <span className="access-badge">Spellings: {totals.spellings}</span>
            <span className={`access-badge ${totals.needsReview > 0 ? 'cities-badge-warn' : ''}`}>
              Need review: {totals.needsReview}
            </span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {notice && <div className="success-message">{notice}</div>}

        {loading ? (
          <div className="access-table-container">
            <table className="access-table">
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: 16 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <h2 className="cities-section-title">Needs review</h2>
            {reviewGroups.length === 0 ? (
              <div className="cities-all-clear">Every city is saved under one spelling. Nothing to merge.</div>
            ) : (
              <div className="cities-review-grid">
                {reviewGroups.map((group) => (
                  <div key={group.key} className="cities-review-card">
                    <div className="cities-review-head">
                      <div>
                        <div className="cities-review-name">{group.label}</div>
                        <div className="access-muted">{plural(group.total, 'listing')}</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => openMerge(group.variants, group.suggested)}
                      >
                        {group.variants.length > 1 ? 'Review & merge' : 'Review & fix'}
                      </button>
                    </div>
                    <div className="cities-review-tags">
                      {group.reasons.map((reason) => (
                        <span key={reason} className="access-tag access-tag-super">{reasonLabel(reason, group)}</span>
                      ))}
                    </div>
                    <ul className="cities-review-list">
                      {group.variants.map((city) => (
                        <li key={city}>
                          <span>{city}</span>
                          <span className="access-muted">{variantByCity.get(city)?.total || 0}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <h2 className="cities-section-title">All city spellings</h2>
            <div className="access-toolbar">
              <input
                className="access-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cities…"
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={openMergeForSelection}
                disabled={selected.length === 0}
              >
                {selected.length > 0 ? `Merge selected (${selected.length})` : 'Merge selected'}
              </button>
              {selected.length > 0 && (
                <button type="button" className="btn btn-secondary" onClick={() => setSelected([])}>
                  Clear selection
                </button>
              )}
              <span className="access-muted">Tick the spellings that are the same place, then merge them.</span>
            </div>

            {filteredVariants.length === 0 ? (
              <div className="empty-state">
                <span className="empty-state-icon">📍</span>
                <h2>No cities found</h2>
                <p>{search ? 'No city matches that search.' : 'Cities appear here once listings are added.'}</p>
              </div>
            ) : (
              <div className="access-table-container">
                <table className="access-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }} aria-label="Select" />
                      <th>City as saved</th>
                      <th>State</th>
                      <th>City coordinates</th>
                      {COUNT_COLUMNS.map((column) => (
                        <th key={column.key} className="cities-number">{column.label}</th>
                      ))}
                      <th className="cities-number">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagination.items.map((variant) => (
                      <tr key={variant.city} className={selected.includes(variant.city) ? 'cities-row-selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(variant.city)}
                            onChange={() => toggleSelected(variant.city)}
                            aria-label={`Select ${variant.city}`}
                          />
                        </td>
                        <td>
                          <span className="access-name">{variant.city}</span>
                          {!variant.isClean && (
                            <span className="access-tag access-tag-super cities-inline-tag">Not a city name</span>
                          )}
                        </td>
                        <td>{variant.state || <span className="access-muted">—</span>}</td>
                        <td>
                          {formatCoordinates(variant.cityLatitude, variant.cityLongitude) || (
                            <span className="access-tag access-tag-inactive">Missing</span>
                          )}
                          {variant.missingCoordinates > 0 && variant.cityLatitude != null && (
                            <span className="access-tag access-tag-inactive cities-inline-tag">
                              {variant.missingCoordinates} missing
                            </span>
                          )}
                        </td>
                        {renderCounts(variant)}
                        <td className="cities-number"><strong>{variant.total}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination {...pagination.props} />
              </div>
            )}

            <h2 className="cities-section-title">Merge history</h2>
            {merges.length === 0 ? (
              <div className="cities-all-clear cities-muted-box">No merges yet.</div>
            ) : (
              <div className="access-table-container">
                <table className="access-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Merged spellings</th>
                      <th>Into</th>
                      <th className="cities-number">Listings</th>
                      <th>By</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merges.map((merge) => (
                      <tr key={merge._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(merge.createdAt)}</td>
                        <td>
                          {(merge.sources || []).map((city) => (
                            <div key={city}>{city}</div>
                          ))}
                        </td>
                        <td className="access-name">{merge.target?.city}</td>
                        <td className="cities-number">{merge.changedCount}</td>
                        <td>{merge.performedByName || '—'}</td>
                        <td>
                          {merge.undoneAt ? (
                            <span className="access-tag access-tag-system" title={formatDateTime(merge.undoneAt)}>
                              Undone
                            </span>
                          ) : (
                            <span className="access-tag access-tag-active">Applied</span>
                          )}
                        </td>
                        <td>
                          <div className="access-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleDownloadBackup(merge)}
                              disabled={busyMergeId === merge._id}
                            >
                              Download backup
                            </button>
                            {canUpdate && !merge.undoneAt && (
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => handleUndo(merge)}
                                disabled={busyMergeId === merge._id}
                              >
                                Undo
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {mergeSources && renderMergeDialog()}
      </div>
    </Layout>
  );
};

export default Cities;
